import { api } from '../../lib/api.js';
import { authState } from '../../lib/auth.js';
import { BaseComponent } from '../../lib/base-component.js';
import { navigateAfterSignIn, SIGN_IN_PATH } from '../../lib/pending-oauth.js';
import { router } from '../../lib/router.js';
import { css, html } from '../../vendor/lit.min.js';

// Import view components
import '../views/login-view.js';
import '../views/logout-view.js';
import '../views/oauth-authorize-view.js';
import '../views/provider-complete-view.js';
import '../views/device-verify-view.js';
import '../views/verify-view.js';
import '../views/profile-view.js';
import '../views/consent-view.js';
import '../views/setup-view.js';
import '../views/invitation-view.js';
import '../views/admin-view.js';
import '../shared/terms-of-service.js';
import '../shared/privacy-policy.js';
import './landing-page.js';

class D3AuthApp extends BaseComponent {
    static properties = {
        loading: { type: Boolean, state: true },
        currentRoute: { type: String, state: true },
        isAuthenticated: { type: Boolean, state: true },
        isAdmin: { type: Boolean, state: true },
        isStaff: { type: Boolean, state: true },
        user: { type: Object, state: true },
        showUserMenu: { type: Boolean, state: true },
        showTenantMenu: { type: Boolean, state: true },
        _staffTenants: { type: Array, state: true },
    };

    static styles = css`
        :host {
            display: block;
            width: 100%;
            min-height: 100vh;
            background-color: #f8f9fa;
        }
        .app-container {
            padding: 1rem 1rem 0;
        }
        .brand {
            font-size: 1.25rem;
            font-weight: bold;
            text-decoration: none;
            color: #333;
        }
        .nav-avatar {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            object-fit: cover;
        }
        .user-menu {
            position: relative;
        }
        .user-menu-toggle {
            cursor: pointer;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            padding: 0.25rem 0.5rem;
            border-radius: 0.375rem;
            border: 1px solid transparent;
        }
        .user-menu-toggle:hover {
            border-color: #dee2e6;
        }
        .user-dropdown {
            position: absolute;
            right: 0;
            top: 100%;
            margin-top: 0.25rem;
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            box-shadow: 0 0.25rem 0.5rem rgba(0,0,0,0.1);
            min-width: 10rem;
            z-index: 1000;
        }
        .user-dropdown a, .user-dropdown button {
            display: block;
            width: 100%;
            padding: 0.5rem 1rem;
            text-align: left;
            text-decoration: none;
            color: #333;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 0.875rem;
        }
        .user-dropdown a:hover, .user-dropdown button:hover {
            background-color: #f8f9fa;
        }
        .user-dropdown .divider {
            border-top: 1px solid #dee2e6;
            margin: 0.25rem 0;
        }
        .tenant-switcher {
            position: relative;
        }
        .tenant-switcher-toggle {
            display: flex;
            align-items: center;
            gap: 0.375rem;
            padding: 0.25rem 0.625rem;
            font-size: 0.8rem;
            border: 1px solid rgba(255,255,255,0.25);
            border-radius: 0.375rem;
            cursor: pointer;
            color: rgba(255,255,255,0.85);
            background: rgba(255,255,255,0.1);
            white-space: nowrap;
        }
        .tenant-switcher-toggle:hover {
            background: rgba(255,255,255,0.15);
            color: #fff;
        }
        .tenant-dropdown {
            position: absolute;
            left: 0;
            top: 100%;
            margin-top: 0.25rem;
            background: white;
            border: 1px solid #dee2e6;
            border-radius: 0.375rem;
            box-shadow: 0 0.25rem 0.5rem rgba(0,0,0,0.15);
            min-width: 12rem;
            z-index: 1000;
            max-height: 20rem;
            overflow-y: auto;
        }
        .tenant-dropdown-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: 0.5rem 0.75rem;
            border: none;
            background: none;
            cursor: pointer;
            font-size: 0.85rem;
            color: #333;
            text-align: left;
        }
        .tenant-dropdown-item:hover { background: #f8f9fa; }
        .tenant-dropdown-item.active { color: #0d6efd; font-weight: 500; }
    `;

    constructor() {
        super();
        this.loading = true;
        this.currentRoute = window.location.pathname;
        this.isAdmin = false;
        this.isStaff = false;
        this.user = null;
        this.showUserMenu = false;
        this.showTenantMenu = false;
        this.isAuthenticated = false;
        this._staffTenants = [];
    }

    async connectedCallback() {
        await super.connectedCallback();

        this._unsubscribeRouter = router.subscribe((state) => {
            this.currentRoute = state.path;
            this.requestUpdate();
        });

        this._unsubscribeAuth = authState.subscribe((state) => {
            this.isAuthenticated = state.isAuthenticated;
            this.isAdmin = (state.permissions || []).includes('read:users');
            const wasStaff = this.isStaff;
            this.isStaff = (state.permissions || []).includes('manage:tenants');
            this.user = state.user;
            this.loading = state.loading;

            // Load tenant list when staff user logs in
            if (this.isStaff && !wasStaff) {
                this._loadStaffTenants();
            }

            // Redirect to landing if not authenticated and on protected route (not OAuth - it handles its own auth)
            if (!state.loading && !state.isAuthenticated && this.currentRoute === '/profile') {
                router.navigate('/');
            }
            // Don't redirect OAuth authorize - it handles its own flow
            if (this.currentRoute.match(/^\/oauth\/[^/]+\/authorize/)) return;
            // Authenticated on the landing or login page: on to the profile, or
            // back to an app still waiting on its authorize request (the Entra
            // redirect can land here with one pending).
            if (
                !state.loading &&
                state.isAuthenticated &&
                (this.currentRoute === '/' || this.currentRoute === SIGN_IN_PATH)
            ) {
                navigateAfterSignIn(sessionStorage, authState.consentsOutstanding);
            }

            this.requestUpdate();
        });

        router.handleRoute();
        await authState.init();

        // Close user menu when clicking outside
        this._onDocClick = (e) => {
            if (
                this.showUserMenu &&
                !e.composedPath().some((el) => el.classList?.contains('user-menu'))
            ) {
                this.showUserMenu = false;
            }
            if (
                this.showTenantMenu &&
                !e.composedPath().some((el) => el.classList?.contains('tenant-switcher'))
            ) {
                this.showTenantMenu = false;
            }
        };
        document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._unsubscribeRouter?.();
        this._unsubscribeAuth?.();
        document.removeEventListener('click', this._onDocClick);
    }

    async _handleLogout() {
        this.showUserMenu = false;
        await authState.logout();
        router.navigate('/');
    }

    async _loadStaffTenants() {
        try {
            const data = await api.get('/api/staff/tenants');
            this._staffTenants = data.tenants || [];
        } catch {
            this._staffTenants = [];
        }
    }

    _handleTenantSwitch(tenantId) {
        authState.setStaffTenant(tenantId || null);
        this.showTenantMenu = false;
    }

    _getActiveTenantName() {
        if (!authState.staffTenantId) return 'My Tenant';
        const t = this._staffTenants.find((t) => t.id === authState.staffTenantId);
        return t ? t.name : 'My Tenant';
    }

    _renderView() {
        const path = this.currentRoute;

        if (path.match(/^\/oauth\/[^/]+\/authorize/))
            return html`<oauth-authorize-view></oauth-authorize-view>`;
        if (path.match(/^\/oauth\/[^/]+\/complete/))
            return html`<provider-complete-view></provider-complete-view>`;
        if (path === '/device') return html`<device-verify-view></device-verify-view>`;
        if (path === '/verify') return html`<verify-view></verify-view>`;
        if (path === '/setup') return html`<setup-view></setup-view>`;
        if (path === '/consent') return html`<consent-view></consent-view>`;
        if (path.startsWith('/invitation/')) return html`<invitation-view></invitation-view>`;
        if (path === '/profile' || path === '/profile/confirm')
            return html`<profile-view></profile-view>`;
        if (path.startsWith('/admin/')) return html`<admin-view></admin-view>`;
        if (path === '/terms')
            return html`<div class="card shadow mt-4"><div class="card-body p-4"><h3 class="mb-4">Terms of Service</h3><terms-of-service></terms-of-service></div></div>`;
        if (path === '/privacy')
            return html`<div class="card shadow mt-4"><div class="card-body p-4"><h3 class="mb-4">Privacy Policy</h3><privacy-policy></privacy-policy></div></div>`;

        if (path === SIGN_IN_PATH) return html`<login-view></login-view>`;
        if (path === '/logout') return html`<logout-view></logout-view>`;

        // Default: landing page
        return html`<landing-page @login="${() => router.navigate(SIGN_IN_PATH)}"></landing-page>`;
    }

    render() {
        if (this.loading) {
            return html`
                <div class="d-flex align-items-center justify-content-center" style="min-height: 100vh;">
                    <div class="text-center">
                        <div class="spinner-border text-primary mb-3" role="status"></div>
                        <p class="text-muted">Loading...</p>
                    </div>
                </div>
            `;
        }

        // Landing page renders full-width without navbar/container
        if (this.currentRoute === '/' && !this.isAuthenticated) {
            return html`<landing-page @login="${() => router.navigate(SIGN_IN_PATH)}"></landing-page>`;
        }

        return html`
            <nav class="navbar ${this.currentRoute.startsWith('/admin') ? 'navbar-dark bg-dark' : 'navbar-light bg-white border-bottom'}">
                <div class="${this.isAuthenticated ? 'container-fluid' : 'container'} d-flex ${this.isAuthenticated ? 'justify-content-between' : 'justify-content-center'} align-items-center">
                    <div class="d-flex align-items-center gap-3">
                        <a class="navbar-brand mb-0" href="/">Dice Driven Design Authentication</a>
                        ${
                            this.isStaff &&
                            this._staffTenants.length > 0 &&
                            this.currentRoute.startsWith('/admin')
                                ? html`
                            <div class="tenant-switcher">
                                <div class="tenant-switcher-toggle" @click="${() => (this.showTenantMenu = !this.showTenantMenu)}">
                                    <i class="bi bi-building" style="font-size: 0.85rem;"></i>
                                    ${this._getActiveTenantName()}
                                    <i class="bi bi-chevron-down" style="font-size: 0.6rem;"></i>
                                </div>
                                ${
                                    this.showTenantMenu
                                        ? html`
                                    <div class="tenant-dropdown">
                                        <button class="tenant-dropdown-item ${!authState.staffTenantId ? 'active' : ''}"
                                            @click="${() => this._handleTenantSwitch(null)}">
                                            My Tenant
                                            ${!authState.staffTenantId ? html`<i class="bi bi-check2"></i>` : ''}
                                        </button>
                                        ${this._staffTenants.map(
                                            (t) => html`
                                            <button class="tenant-dropdown-item ${authState.staffTenantId === t.id ? 'active' : ''}"
                                                @click="${() => this._handleTenantSwitch(t.id)}">
                                                ${t.name}
                                                ${authState.staffTenantId === t.id ? html`<i class="bi bi-check2"></i>` : ''}
                                            </button>
                                        `,
                                        )}
                                    </div>
                                `
                                        : ''
                                }
                            </div>
                        `
                                : ''
                        }
                    </div>
                    ${
                        this.isAuthenticated && this.user
                            ? html`
                        <div class="d-flex align-items-center gap-2">
                            <div class="user-menu">
                                <div class="user-menu-toggle ${this.currentRoute.startsWith('/admin') ? 'text-light' : ''}" @click="${() => (this.showUserMenu = !this.showUserMenu)}">
                                    ${
                                        this.user.picture
                                            ? html`<img src="${this.user.picture}" class="nav-avatar" alt="">`
                                            : html`<i class="bi bi-person-circle" style="font-size: 1.5rem;"></i>`
                                    }
                                    <span class="d-none d-md-inline" style="font-size: 0.875rem;">${this.user.username || this.user.email}</span>
                                    <i class="bi bi-chevron-down" style="font-size: 0.7rem;"></i>
                                </div>
                                ${
                                    this.showUserMenu
                                        ? html`
                                    <div class="user-dropdown">
                                        <a href="/profile" @click="${() => (this.showUserMenu = false)}">Profile</a>
                                        ${this.isAdmin ? html`<a href="/admin/applications" @click="${() => (this.showUserMenu = false)}">Admin</a>` : ''}
                                        <div class="divider"></div>
                                        <button @click="${this._handleLogout}">Sign Out</button>
                                    </div>
                                `
                                        : ''
                                }
                            </div>
                        </div>
                    `
                            : ''
                    }
                </div>
            </nav>
            <div class="app-container">
                ${
                    this.currentRoute.startsWith('/admin')
                        ? this._renderView()
                        : html`
                    <div class="container">
                        ${this._renderView()}
                    </div>
                `
                }
            </div>
            <footer class="text-center mt-2">
                <small class="text-muted">&copy; ${new Date().getFullYear()} Dice Driven Design LLC${authState._config?.version ? html` &middot; ${authState._config.version}` : ''}</small>
            </footer>
        `;
    }
}

customElements.define('d3-auth-app', D3AuthApp);
export { D3AuthApp };
